import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min';
import { Base } from './_base.model';

export class EventProfile extends Base {
  id: string;
  common_event: any;
  prefix: string;
  meeting_type_id: number;
  meal_type: string | null;
  speaker_count: number;
  topic_id: number;
  sub_topic_id: number | null;
  topic_count: number;
  event_topics: any[];
  status_id: string;
  sub_status_id: string;
  program_series: string;
  audience_type: string;
  attendee_format: string;
  speaker_format: string;
  planner_staff_id: string;
  venue_staff_id: string;
  faculty_staff_id: string;
  travel_staff_id: string;
  reconciliation_staff_id: string;
  planner_shadow_id: string;
  venue_shadow_id: string;
  faculty_shadow_id: string;
  travel_shadow_id: string;
  reconciliation_shadow_id: string;
  area_id: number;
  fiscal_year_id: string;
  job_number: string | null;
  eo_of_record_id: number;
  reconciled_at: Date | null;
  budget_status_id: string;
  shared_areas: any;
  public_calendar: string;
  only_hcps: string;
  only_hcp_specialties: string;
  agency_id: string;
  registration_code: string;
  rsvp_by_date: Date | null;
  request: any;
  do_not_cancel: boolean;
  event_date_tbd: boolean;

  deserialize(obj: any) {
    Object.assign(this, obj);
    if (this.reconciled_at) {
      this.reconciled_at = moment(this.reconciled_at).toDate();
    }
    if (this.rsvp_by_date) {
      this.rsvp_by_date = moment(this.rsvp_by_date).toDate();
    }
    if (this.common_event.start_at) {
      const eventStartAt = moment(this.common_event.start_at).tz(this.common_event.timezone_id);
      this.common_event.event_start_at = eventStartAt.toDate();
      this.common_event.event_date = eventStartAt.format('MM-DD-YYYY');
      this.common_event.event_time = eventStartAt.format('HH:mm');
      // Initialize end_time from end_at if exists
      if (this.common_event.end_at) {
        const eventEndAt = moment(this.common_event.end_at).tz(this.common_event.timezone_id);
        this.common_event.end_time = eventEndAt.format('HH:mm');
      }
      // Initialize arrival_time from arrival_at if exists, otherwise set to 30 minutes before start
      if (this.common_event.arrival_at) {
        const arrivalAt = moment(this.common_event.arrival_at).tz(this.common_event.timezone_id);
        this.common_event.arrival_time = arrivalAt.format('HH:mm');
      } else if (this.common_event.start_at) {
        // Set arrival time to 30 minutes before start time
        const arrivalAt = eventStartAt.clone().subtract(30, 'minutes');
        this.common_event.arrival_time = arrivalAt.format('HH:mm');
      }
    }
    return this;
  }

  setEventDate(value) {
    this.common_event.event_date = moment(value).format('YYYY-MM-DD');
  }

  setEventTime(value) {
    this.common_event.event_time = moment(value).format('HH:mm');
  }

  setEndTime(value) {
    this.common_event.end_time = moment(value).format('HH:mm');
  }

  setArrivalTime(value) {
    this.common_event.arrival_time = moment(value).format('HH:mm');
  }

  /**
   * Parse event_date + HH:mm into a Date for timepickers (supports MM-DD-YYYY and YYYY-MM-DD).
   */
  private dateAndTimeToLocalDate(dateStr: string, timeStr: string): Date | null {
    if (!dateStr || !timeStr) {
      return null;
    }
    if (dateStr.includes('-') && dateStr.split('-')[0].length === 2) {
      const [month, day, year] = dateStr.split('-');
      return new Date(`${year}-${month}-${day} ${timeStr}`);
    }
    return new Date(`${dateStr} ${timeStr}`);
  }

  getEndDate() {
    if (this.common_event.event_date && this.common_event.end_time) {
      return this.dateAndTimeToLocalDate(this.common_event.event_date, this.common_event.end_time);
    }
    return null;
  }

  getArrivalDate() {
    if (this.common_event.event_date && this.common_event.arrival_time) {
      return this.dateAndTimeToLocalDate(this.common_event.event_date, this.common_event.arrival_time);
    }
    return null;
  }

  setEventDateTimeUTC(numberOfEventHours: number) {
    if (this.common_event.event_date && this.common_event.event_time && this.common_event.timezone_id) {
      this.common_event.start_at = moment.tz(`${this.common_event.event_date} ${this.common_event.event_time}`, this.common_event.timezone_id).toDate();
      // Use end_time if provided, otherwise calculate from duration_hours
      if (this.common_event.end_time) {
        this.common_event.end_at = moment.tz(`${this.common_event.event_date} ${this.common_event.end_time}`, this.common_event.timezone_id).toDate();
      } else {
        this.common_event.end_at = moment.tz(`${this.common_event.event_date} ${this.common_event.event_time}`, this.common_event.timezone_id).add(numberOfEventHours, 'hours').toDate();
      }
      // Set arrival_at if arrival_time is set
      if (this.common_event.arrival_time && this.common_event.timezone_id) {
        this.common_event.arrival_at = moment.tz(`${this.common_event.event_date} ${this.common_event.arrival_time}`, this.common_event.timezone_id).toDate();
      }
    } else {
      this.common_event.start_at = null;
      this.common_event.end_at = null;
      this.common_event.arrival_at = null;
    }
  }

  getEventDate() {
    if (!this.common_event.event_date || !this.common_event.event_time) {
      return null;
    }
    return this.dateAndTimeToLocalDate(this.common_event.event_date, this.common_event.event_time);
  }
}
